import { useEffect, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import {
    askSupportBot,
    ISupportBotTurn,
} from 'services/supportBot'

interface IProps {
    show: boolean
    onHide: () => void
    /** Opens the ordinary human-support options (call / Eitaa / Telegram). */
    onWantsHuman: () => void
}

const GREETING =
    'سلام! من پشتیبان هوشمند همیان هستم. می‌توانید دربارهٔ صندوق، وام، اقساط، ' +
    'سرمایه و پرداخت‌هایتان از من بپرسید.'

const SUGGESTIONS = [
    'سرمایه‌ام در صندوق‌ها چقدر است؟',
    'اقساط پرداخت‌نشده‌ام کدام‌ها هستند؟',
    'کارمزد همیان چقدر است؟',
]

/**
 * Everything a failed request is allowed to say in an event.
 *
 * A closed enum and the status, never the server's own error text — this
 * endpoint's messages can quote the question back, and the question is the one
 * thing that must never reach analytics.
 */
const describeFailure = (error: any) => {
    const status = error?.response?.status ?? null
    const kind =
        error?.code === 'ECONNABORTED'
            ? 'timeout'
            : !status
            ? 'network'
            : status === 400
            ? 'http_400'
            : status === 503
            ? 'http_503'
            : status >= 500
            ? 'http_5xx'
            : 'unknown'
    return { error_kind: kind, status }
}

const SupportChatModal = ({ show, onHide, onWantsHuman }: IProps) => {
    const [turns, setTurns] = useState<ISupportBotTurn[]>([])
    const [draft, setDraft] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [didFail, setDidFail] = useState(false)
    const [wasEscalated, setWasEscalated] = useState(false)
    const bottomRef = useRef<HTMLDivElement>(null)
    const openedAtRef = useRef(0)
    const askedCountRef = useRef(0)
    const lastOutcomeRef = useRef('none')

    // The answer takes several seconds, so the newest bubble is usually below
    // the fold by the time it lands.
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [turns, isLoading])

    // What the close event reports is per-visit, and the component stays
    // mounted between visits, so the counters start over on the way in.
    useEffect(() => {
        if (!show) return
        openedAtRef.current = Date.now()
        askedCountRef.current = 0
        lastOutcomeRef.current = 'none'
    }, [show])

    const send = async (text: string, isSuggestion = false) => {
        const question = text.trim()
        if (!question || isLoading) return

        setDraft('')
        setDidFail(false)
        setWasEscalated(false)
        // History is taken from the state we had BEFORE appending, so the new
        // question is not sent twice.
        const history = turns.slice(-6)
        setTurns((previous) => [
            ...previous,
            { role: 'user', content: question },
        ])
        setIsLoading(true)

        const turnIndex = askedCountRef.current
        askedCountRef.current += 1
        posthog.capture('support_bot_question_asked', {
            turn_index: turnIndex,
            question_length: question.length,
            is_suggestion: isSuggestion,
            is_followup: turns.length > 0,
            history_turns: history.length,
        })

        const startedAt = Date.now()
        try {
            const data = await askSupportBot({ question, history })
            setTurns((previous) => [
                ...previous,
                { role: 'assistant', content: data.answer },
            ])
            setWasEscalated(data.escalated)
            // The server's own failures — spent time budget, provider error,
            // empty completion, too many tool rounds — all arrive as a 200 with
            // a canned reply, so a resolved promise is not evidence of an
            // answer and `outcome` is what tells them apart. Read defensively:
            // a backend older than this client sends neither field.
            const outcome = data.outcome ?? 'unknown'
            lastOutcomeRef.current = outcome
            posthog.capture('support_bot_answered', {
                escalated: data.escalated,
                tools_used: data.tools_used,
                tool_calls_count: data.tools_used?.length ?? 0,
                answer_ms: Date.now() - startedAt,
                answer_length: data.answer?.length ?? 0,
                turn_index: turnIndex,
                outcome,
                ...(data.escalation_reason
                    ? { escalation_reason: data.escalation_reason }
                    : {}),
            })
        } catch (error) {
            setDidFail(true)
            lastOutcomeRef.current = 'failed'
            posthog.capture('support_bot_failed', {
                ...describeFailure(error),
                answer_ms: Date.now() - startedAt,
                turn_index: turnIndex,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const close = () => {
        posthog.capture('support_bot_closed', {
            turns_count: turns.length,
            asked_count: askedCountRef.current,
            last_outcome: lastOutcomeRef.current,
            was_escalated: wasEscalated,
            seconds_open: openedAtRef.current
                ? Math.round((Date.now() - openedAtRef.current) / 1000)
                : 0,
        })
        setTurns([])
        setDraft('')
        setDidFail(false)
        setWasEscalated(false)
        onHide()
    }

    return (
        <Modal show={show} onHide={close} centered scrollable>
            <div className="flex flex-col text-xs" style={{ height: '70vh' }}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="font-bold">پشتیبان هوشمند</span>
                    <span className="text-[10px] text-gray-400">
                        نسخهٔ آزمایشی
                    </span>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                    <div className="bg-gray-100 rounded-lg p-3 mb-3 leading-6">
                        {GREETING}
                    </div>

                    {turns.length === 0 && !isLoading && (
                        <div className="flex flex-col gap-2 mb-3">
                            {SUGGESTIONS.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => send(item, true)}
                                    className="text-right border border-green-1 text-green-1 rounded-lg px-3 py-2"
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                    )}

                    {turns.map((turn, index) => (
                        <div
                            key={index}
                            className={
                                turn.role === 'user'
                                    ? 'flex justify-start mb-3'
                                    : 'flex justify-end mb-3'
                            }
                        >
                            <div
                                className={
                                    turn.role === 'user'
                                        ? 'bg-green-1 text-white rounded-lg p-3 max-w-[85%] leading-6'
                                        : 'bg-gray-100 rounded-lg p-3 max-w-[85%] leading-6'
                                }
                                // The server strips Markdown, so plain text with
                                // preserved newlines is exactly what arrives.
                                style={{ whiteSpace: 'pre-wrap' }}
                            >
                                {turn.content}
                            </div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="flex justify-end mb-3">
                            <div className="bg-gray-100 rounded-lg px-4 py-3 text-green-1">
                                <DotsLoading />
                            </div>
                        </div>
                    )}

                    {didFail && (
                        <div className="text-red-500 text-center my-2 leading-6">
                            پاسخی دریافت نشد. لطفاً دوباره تلاش کنید یا با
                            پشتیبانی تماس بگیرید.
                        </div>
                    )}

                    {/* Whenever the backend decided a human is needed, make
                        reaching one a single tap rather than a instruction. */}
                    {(wasEscalated || didFail) && (
                        <button
                            type="button"
                            onClick={() => {
                                // Captured before close(), which resets
                                // everything this event is made of.
                                posthog.capture(
                                    'support_bot_escalation_accepted',
                                    {
                                        reason: wasEscalated
                                            ? 'escalated'
                                            : 'failed',
                                        turn_index: Math.max(
                                            askedCountRef.current - 1,
                                            0
                                        ),
                                    }
                                )
                                close()
                                onWantsHuman()
                            }}
                            className="w-full bg-green-1 text-white rounded-lg py-2 my-2"
                        >
                            تماس با پشتیبانی
                        </button>
                    )}

                    <div ref={bottomRef} />
                </div>

                <div className="border-t px-3 py-2">
                    <form
                        onSubmit={(event) => {
                            event.preventDefault()
                            send(draft)
                        }}
                        className="flex items-center gap-2"
                    >
                        <input
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            placeholder="سوالتان را بنویسید…"
                            maxLength={1000}
                            disabled={isLoading}
                            className="flex-1 border rounded-lg px-3 py-2 outline-none"
                        />
                        <button
                            type="submit"
                            disabled={isLoading || !draft.trim()}
                            className="bg-green-1 text-white rounded-lg px-4 py-2 disabled:opacity-40"
                        >
                            ارسال
                        </button>
                    </form>
                    <p
                        className="text-[10px] text-gray-400 text-center mt-2 mb-0 cursor-pointer"
                        onClick={close}
                    >
                        بستن
                    </p>
                </div>
            </div>
        </Modal>
    )
}

export default SupportChatModal
